/**
 * The one way the app loads a module on demand.
 *
 * A module loaded with import() lives in its own file, named by a hash of
 * its content. Each deploy replaces every file, so a page opened before a
 * deploy asks for a file that no longer exists, and the import fails. That
 * is not the feature failing: a reload fetches the new files. So a failed
 * load becomes a ModuleLoadError, which every caller shows as its reload
 * sentence (errorCopy.ts, key 'module_stale'), and the failure is reported
 * to the Errors screen without anything the person wrote: the surface, the
 * error's name, the page path. A caller tells the failure apart with
 * isModuleLoadError and shows its own surface's 'module_stale' sentence.
 *
 * tools/__tests__/dynamic-imports.test.ts keeps every import() in src/
 * inside loadModule.
 */

import { defaultTenant } from '@/lib/auth';
import { mintReference, reportPageCrash } from '@/lib/appErrors';

/** The failures loadModule raised, told apart without a new error type. */
const moduleLoadFailures = new WeakSet<object>();

/** Whether `e` is a module that did not load (a page opened before a deploy). */
export function isModuleLoadError(e: unknown): boolean {
  return typeof e === 'object' && e !== null && moduleLoadFailures.has(e);
}

function tenantOfPage(): string {
  const m = /^\/t\/([^/]+)\//.exec(window.location.pathname);
  return m ? decodeURIComponent(m[1]!) : defaultTenant;
}

/** Report a failure on a surface to the Errors screen, content-free. */
export function reportSurfaceFailure(surface: string, errorCode: string, err: unknown): void {
  const name = err instanceof Error ? err.name : typeof err;
  reportPageCrash(tenantOfPage(), {
    reference: mintReference(),
    route: window.location.pathname,
    error_code: errorCode,
    detail: `${surface}: ${name}`,
  });
}

export async function loadModule<T>(surface: string, load: () => Promise<T>): Promise<T> {
  try {
    return await load();
  } catch (e) {
    reportSurfaceFailure(surface, 'module_load_failed', e);
    const failure = e instanceof Error ? e : new Error(`${surface}: a module did not load`);
    moduleLoadFailures.add(failure);
    throw failure;
  }
}
